import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AppRoute } from "app/app-layout/constants/app-route.enum";
import { LicenseRequestInterface } from "app/core/authentication/models/license-request.model";
import { AuthService } from "app/core/authentication/services/auth.service";
import { RouterRequest } from "app/core/browser/models/router-request.model";
import { BrowserService } from "app/core/browser/services/browser.service";
import { Error } from "app/core/error-handler/models";
import { DefaultLanguageStringsService } from "app/core/language/services/default-language-strings.service";
import { LanguageService } from "app/core/language/services/language.service";
import { UserInterface } from "app/core/user/models";
import { EndUserAgreementComponent } from "app/login/components/end-user-agreement/end-user-agreement.component";
import { SSOAccessOnlyComponent } from "app/login/components/sso-access-only/sso-access-only.component";
import { PasswordResetComponent } from "app/login/components/password-reset/password-reset.component";
import { PrivacyPolicyComponent } from "app/login/components/privacy-policy/privacy-policy.component";
import { TermsOfUseComponent } from "app/login/components/terms-of-use/terms-of-use.component";
import { GlobalConstants } from "app/shared/constants/globalconstants";
import { SiteLogoService } from "app/site-logo/services/site-logo.service";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { skip, take, takeUntil } from "rxjs/operators";
import { MFAComponent } from "../mfa/mfa.component";
@Component({
  selector: "cz-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
  public username: FormControl = new FormControl("", [Validators.required]);
  public password: FormControl = new FormControl("", [Validators.required]);
  public cookieControl: FormControl = new FormControl(false, [
    Validators.requiredTrue,
  ]);
  public resetUsername: FormControl = new FormControl("", [
    Validators.required,
  ]);
  public licenseKeyControl: FormControl = new FormControl("", [
    Validators.required,
  ]);
  public hidePassword: boolean = true;
  public displayLoginContent: boolean = false;
  public displayForgotPasswordContent: boolean = false;
  public displayForgotPasswordSubmittedContent: boolean = false;
  public isResetPasswordError: boolean = false;
  public displayUsernameHelpContent: boolean = false;
  public displayNoAccountHelpContent: boolean = false;
  public displayLicenseExpiredContent: boolean = false;
  public languageLoaded: boolean = false;
  public authError$: Observable<Error>;
  public readonly language$: BehaviorSubject<{ [key: string]: string }>;

  // languageArray variable declaration
  public languageArray: any;
  public loginGroup: FormGroup;
  public resetPasswordGroup: FormGroup;
  public adminToken: string;
  public logoHref: string;
  public selectedLanguage: string;
  public minDays: any;
  private readonly _cookieName: string = "Allow-Cookies";
  // _cookieName is moved to localstoorage for cookies
  private readonly destroy$: Subject<boolean> = new Subject<boolean>();
  public passwordSiteSettingsInfo: any = [];
  private isLoggingIn: boolean = false;
  private _privacyDialogOpened: boolean = false;
  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _browserService: BrowserService,
    private readonly _authService: AuthService,
    public dialog: MatDialog,
    private readonly _changeDetector: ChangeDetectorRef,
    private readonly _cookieService: CookieService,
    private readonly _translate: TranslateService,
    private readonly _languageService: LanguageService,
    private readonly _siteLogoService: SiteLogoService,
    // private readonly  _localStorage: WindowLocalStorage,
    public _GlobalConstants: GlobalConstants,
    public readonly _defaultLanguageService: DefaultLanguageStringsService
  ) {
    // Assing empty array
    this.languageArray = [];
    this.language$ = new BehaviorSubject<{ [key: string]: string }>(undefined);
  }

  public ngOnInit(): void {
    if (location.protocol === "http:" && location.hostname !== "localhost") {
      window.location.href = location.href.replace("http", "https");
    }

    this.loginGroup = new FormGroup({
      username: this.username,
      password: this.password,
      cookieControl: this.cookieControl,
    });
    this.cookieControl.setValue(
      localStorage.getItem(this._cookieName) === "true"
    );
    this.cookieControl.markAsTouched();

    this.resetPasswordGroup = new FormGroup({
      username: this.resetUsername,
    });

    // Check for license on first load.
    this._authService
      .licenseCheck()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLicensed: boolean) => {
        if (!isLicensed && typeof isLicensed !== "undefined") {
          this.showLicenseExpired();
        } else {
          const resetPassword: string =
            this._route.snapshot.queryParamMap.get("resetPassword");
          const openPrivacy: string =
            this._route.snapshot.queryParamMap.get("openPrivacyPolicy");
          if (resetPassword === "true") {
            this.showPasswordHelp();
          } else if (openPrivacy === "true") {
            this.showLoginContent();
            setTimeout(() => {
              if (!this._privacyDialogOpened) {
                this.privacyPolicyDialog();
              }
            }, 1000); // 2 second delay
          } else {
            this.showLoginContent();
          }
        }

        this._changeDetector.markForCheck();
      });

    // Subscribe to receive auth errors.
    this.authError$ = this._authService
      .getError()
      .pipe(takeUntil(this.destroy$));

    // Get available languages
    this._languageService
      .getLanguages()
      .pipe(takeUntil(this.destroy$))
      .subscribe((language: any) => {
        // Assign llanguage data0
        this.languageArray = Object.values(language);
        if (
          typeof language === "undefined" ||
          Object.keys(language).length === 0
        ) {
          return;
        }

        let cultureCookie: string = this._cookieService.get("Culture");

        /* const defaultLanguage: any = this.languageArray.find((item: any) => item.isDefault === true);
        if (defaultLanguage) {
          this._defaultLanguageService.defaultLanguageCulture = defaultLanguage.culture;
        } */
        // get cookie value exist in languageArray
        const languageKey: any = this.languageArray.find(
          (item: any) => item.culture === cultureCookie
        );
        // Udating default language as cookie if cookie value exist in languageArray
        if (typeof languageKey === "undefined" || languageKey.length === 0) {
          this.languageArray.map((item: any) => {
            if (item.isDefault) {
              if (localStorage.getItem(this._cookieName)) {
                this._cookieService.delete("Culture", undefined, "/");
                this._cookieService.set(
                  "Culture",
                  item.culture,
                  undefined,
                  "/"
                );
              }
              cultureCookie = item.culture;
            }
          });
        }
        // add language keys to translation service
        if (this._translate.getLangs().length < 1) {
          const langArr: string[] = this.languageArray.map(
            (item: any) => item.culture
          ); // Object.keys(language);
          this._translate.addLangs(langArr);
        }
        // set default language to culture cookie language and use it
        this._translate.setDefaultLang(cultureCookie);
        this._translate.use(cultureCookie);
        this.selectedLanguage = cultureCookie;

        this.language$.next(language);
      });

    this._siteLogoService
      .loadSiteLogo()
      .pipe(takeUntil(this.destroy$))
      .subscribe((href: string) => {
        if (!href) {
          return;
        }
        this.logoHref = href;
      });
    // Close all open dialogs that may persist from the previous session.
    this.dialog.closeAll();
    this._authService.getPasswordSettingsData().subscribe((res: any) => {
      this.passwordSiteSettingsInfo = res;
    });
  }
  public passwordPatterns() {
    if (this.passwordSiteSettingsInfo.requiredCharacterSets != "") {
      const str = this.passwordSiteSettingsInfo.requiredCharacterSets;
      const spericalChars = "!@#$%^&*)(+=._-";
      const arr = str.split(",");
      let regex = "";
      for (let i = 0; i < arr.length; i++) {
        if (arr[i] == "special") {
          regex += "(?=.*[" + spericalChars + "])";
        } else {
          regex += "(?=.*[" + arr[i] + "])";
        }
      }
      const finalRegex = new RegExp(regex);
      this.loginGroup.controls.password.setValidators(
        Validators.pattern(finalRegex)
      );
      this.loginGroup.controls.password.updateValueAndValidity();
    } else {
      this.loginGroup.controls.password.clearValidators();
      this.loginGroup.controls.password.updateValueAndValidity();
    }
  }
  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public login(): void {
    if (!this.loginGroup.valid) {
      return;
    }
    this.isLoggingIn = true;
    localStorage.setItem("messageShown", "false");
    this._authService
      .login(this.username.value, this.password.value)
      .pipe(skip(1), take(1))
      .subscribe((user: UserInterface) => {
        if (user.forcePassword) {
          this.passswordReset(user.id, user.tcAccept);
        } else if (this.passwordSiteSettingsInfo.enableMFA) {
          this.showMFADialog(user.id);
        } else {
          this.handleSuccessfulLogin(user);
        }
      });
  }

  private showMFADialog(userId: number): void {
    const dialogRef = this.dialog.open(MFAComponent, {
      width: "400px",
      data: { userId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this._authService.getLoggedInUser().subscribe((user) => {
          this.handleSuccessfulLogin(user);
        });
      }
      this.isLoggingIn = false;
    });
  }

  private handleSuccessfulLogin(user: UserInterface): void {
    this.passwordPatterns();
    if (user.ssoAccessOnly) {
      this.showAccessOnlyDialog();
    } else if (user.tcAccept && !user.forcePassword) {
      sessionStorage.setItem("isUserLoggedIn", "1");
      if (this.loginGroup.controls.password.errors != null) {
        this.passswordReset(user.id, user.tcAccept);
        this.username.reset();
        this.password.reset();
        return;
      }
      if (
        this.loginGroup.controls.password.value.length <
        this.passwordSiteSettingsInfo.minimumLength
      ) {
        this.passswordReset(user.id, user.tcAccept);
        this.username.reset();
        this.password.reset();
        return;
      }
      if (!user || typeof user.tcAccept === "undefined") {
        return;
      }
      const backupLanguageIndex = this.languageArray.findIndex(
        (x: any) => x.isBackupTranslation === true
      );
      this._GlobalConstants.backupLanguageId = 0;
      if (backupLanguageIndex !== -1) {
        this._GlobalConstants.backupLanguageId =
          this.languageArray[backupLanguageIndex].languageId;
      }
      this._GlobalConstants.cultureCookieId = 0;
      const cultureCookieIndex = this.languageArray.findIndex(
        (x: any) => x.culture === this._cookieService.get("Culture")
      );
      if (cultureCookieIndex !== -1) {
        this._GlobalConstants.cultureCookieId =
          this.languageArray[cultureCookieIndex].languageId;
      }
      // load all default strings.
      this.loadDefaultLanguageStrings();
      this.goToDashboard();
    } else {
      if (user.forcePassword) {
        sessionStorage.setItem("isUserLoggedIn", "1");
        this.passswordReset(user.id, user.tcAccept);
        this.username.reset();
        this.password.reset();
      } else if (!user.tcAccept) {
        sessionStorage.setItem("isUserLoggedIn", "1");
        this.endUserAgreementDialog();
      }
    }
  }

  public resetPassword(): void {
    if (!this.resetPasswordGroup.valid) {
      return;
    }
    this.resetPasswordGroup.controls["username"].setErrors(null);
    this._authService.passwordReset(this.resetUsername.value).subscribe(
      () => {
        this.resetUsername.setValue("");
        this.resetUsername.markAsUntouched();
        this.displayForgotPasswordContent = false;
        this.displayForgotPasswordSubmittedContent = true;
        this.resetPasswordGroup.controls["username"].setErrors(null);
      },
      (error: any) => {
        if (error.error.message === "SSOAccessOnly") {
          this.showAccessOnlyDialog();
          this.isResetPasswordError = false;
        } else if (
          error.error.message ===
          "Password Updated Recently..Can't Update Password"
        ) {
          this._authService.getPasswordSettingsData().subscribe(
            (res: any) => {
              this.minDays = res["minimumDaysForPasswordAge"];
              this.resetPasswordGroup.controls["username"].setErrors({
                incorrect: true,
              });
              this.isResetPasswordError = true;
              this._changeDetector.markForCheck();
            },
            (error: any) => {
              // Handle the error if necessary
            }
          );
        } else {
          this.resetPasswordGroup.controls["username"].setErrors({
            serverError: true,
          });
          this.isResetPasswordError = true;
          this._changeDetector.markForCheck();
        }
      }
    );
  }

  public refreshLanguages(language: string): void {
    if (localStorage.getItem(this._cookieName)) {
      this._languageService.getLanguages();
    }
    this.selectedLanguage = language;
  }

  public showLoginCard(): boolean {
    return (
      this.displayLoginContent ||
      this.displayForgotPasswordContent ||
      this.displayUsernameHelpContent ||
      this.displayNoAccountHelpContent ||
      this.displayLicenseExpiredContent ||
      this.displayForgotPasswordSubmittedContent ||
      this.isResetPasswordError
    );
  }

  public showUserNameHelp(): void {
    this.displayLoginContent = false;
    this.displayUsernameHelpContent = true;
  }

  public showPasswordHelp(): void {
    this.displayLoginContent = false;
    this.displayForgotPasswordContent = true;
  }

  public showAccountHelp(): void {
    this.displayLoginContent = false;
    this.displayNoAccountHelpContent = true;
  }

  public showLicenseExpired(): void {
    this.displayLoginContent = false;
    this.displayLicenseExpiredContent = true;
  }

  public showLoginContent(): void {
    this.username.reset();
    this.password.reset();
    this.resetUsername.reset();
    this.displayLoginContent = true;
    this.displayForgotPasswordContent = false;
    this.displayForgotPasswordSubmittedContent = false;
    this.displayUsernameHelpContent = false;
    this.displayNoAccountHelpContent = false;
    this.displayLicenseExpiredContent = false;
  }

  public toggleCookieAcceptance(): void {
    // If the user un-checks the acceptance, delete the cookie.
    if (!this.cookieControl.value) {
      localStorage.removeItem(this._cookieName);
      this._cookieService.delete("Culture");
      return;
    }
    // If they accept and there is no cookie, set one.
    if (!localStorage.getItem(this._cookieName)) {
      const oneYearFromNow: Date = new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      );
      this._cookieService.set("Culture", this.selectedLanguage, undefined, "/");
      localStorage.setItem(this._cookieName, "true");
    }
  }

  public showAccessOnlyDialog(): void {
    this.dialog.open(SSOAccessOnlyComponent, {
      height: "480px",
      width: "700px",
      data: {
        messageId: 4,
      },
    });
  }

  public endUserAgreementDialog(): void {
    this.dialog.open(EndUserAgreementComponent, {
      height: "480px",
      width: "700px",
      data: {
        username: this.username.value,
        password: this.password.value,
      },
    });
  }

  public termsOfUseDialog(): void {
    this.dialog.open(TermsOfUseComponent, {
      height: "480px",
      width: "700px",
    });
  }

  public privacyPolicyDialog(): void {
    if (this._privacyDialogOpened) {
      return;
    }
    this._privacyDialogOpened = true;
    const ref = this.dialog.open(PrivacyPolicyComponent, {
      height: "480px",
      width: "700px",
    });
    ref.afterClosed().subscribe(() => (this._privacyDialogOpened = false));
  }

  public updateLicenseKey(): void {
    if (!this.licenseKeyControl.valid) {
      return;
    }
    const licenseKeyData: LicenseRequestInterface = {
      licenseKey: this.licenseKeyControl.value,
    };
    this._authService.licenseUpdate(licenseKeyData);
  }

  /**
   * Reset passsword dialog
   */
  public passswordReset(id: any, tc: boolean): void {
    this.isLoggingIn = true;
    this.dialog.open(PasswordResetComponent, {
      height: "480px",
      width: "531px",
      data: {
        userId: id,
        type: "user",
        username: this.username.value,
        password: this.password.value,
        tcAccept: tc,
      },
    });
  }

  private goToDashboard(): void {
    localStorage.setItem("beenToDashboard", "no");
    const navRequest: RouterRequest = new RouterRequest({
      route: AppRoute.myZone,
    });
    this._browserService.to(navRequest);
  }

  private loadDefaultLanguageStrings(): void {
    this._defaultLanguageService
      .loadDefaultLanguageStrings()
      .pipe()
      .subscribe((result: any) => {
        this._defaultLanguageService.setDefaultLanguageStrings(result);
      });
  }
}
